import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, QrCode, RefreshCw, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import Textarea from '@/components/ui/textarea.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  closeReception,
  getFojaDetail,
  registerFojaScan,
  startReception,
} from '@/services/fojas.js';
import { getFojaStatusPresentation } from '@/utils/fojaOperationalStatus.js';
import { receptionNotice } from './recepcionNotice.js';

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;


const MODE_LABELS = {
  manual_por_guia: 'Manual por guía',
  marcar_todo: 'Marcar todo',
  qr_obligatorio: 'QR obligatorio',
};

const formatNumber = (value, digits = 0) =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));

const getSucursalInfo = (auth) => {
  const sucursal = auth?.sucursalActiva || auth?.sucursal || null;
  return {
    id: sucursal?.id || sucursal?.sucursal_id || null,
    nombre: sucursal?.nombre || sucursal?.descripcion || 'Sucursal activa',
  };
};

const STATUS_MESSAGE_CLASSNAMES = {
  error: 'rounded-2xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] px-4 py-3 text-sm text-[rgb(var(--danger))]',
  success: 'rounded-2xl border border-[rgba(var(--success),0.28)] bg-[rgba(var(--success),0.12)] px-4 py-3 text-sm text-[rgb(var(--success))]',
  info: 'rounded-2xl border border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.1)] px-4 py-3 text-sm text-[rgb(var(--fg))]',
};

export default function RecepcionFoja() {
  const { id } = useParams();
  const auth = useAuth();
  const sucursal = getSucursalInfo(auth);
  const scanInputRef = useRef(null);

  const [detail, setDetail] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [differences, setDifferences] = useState({});
  const [scanValue, setScanValue] = useState('');
  const [scanMessage, setScanMessage] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadDetail = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const data = await getFojaDetail(id);
      setDetail(data);
      setSelectedIds(
        Array.isArray(data?.guias)
          ? data.guias.filter((item) => Number(item.recibida || 0) === 1).map((item) => item.guia_id)
          : [],
      );
      setObservaciones(data?.foja?.observaciones || '');
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'No se pudo cargar la foja.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadDetail();
  }, [id]);

  const foja = detail?.foja || null;
  const guias = detail?.guias || [];
  const modoRecepcion = foja?.modo_recepcion || 'manual_por_guia';
  const scansDone = Number(detail?.stats?.total_guias_escaneadas || 0);
  const totalGuias = Number(detail?.stats?.total_guias || guias.length || 0);
  const pendingGuias = guias.filter((item) => !selectedIds.includes(item.guia_id));
  const receptionStarted = (detail?.historial || []).some((item) => item.evento === 'FOJA_RECEPCION_INICIADA');
  const canOperateReception = ['despachada_confirmada', 'recibida_parcial'].includes(foja?.estado);
  const notice = foja ? receptionNotice(foja.estado) : null;

  const totals = useMemo(
    () =>
      guias.reduce(
        (acc, item) => {
          acc.bultos += Number(item.bultos || 0);
          acc.kilos += Number(item.kg_reales || 0);
          acc.valor += Number(item.valor_declarado || 0);
          return acc;
        },
        { bultos: 0, kilos: 0, valor: 0 },
      ),
    [guias],
  );

  const canMarkAll = modoRecepcion === 'marcar_todo' && !['recibida', 'recibida_parcial'].includes(foja?.estado);
  const canToggle = modoRecepcion !== 'qr_obligatorio' && !['recibida', 'recibida_parcial'].includes(foja?.estado);

  const toggleGuide = (guiaId) => {
    if (!canToggle) return;
    setSelectedIds((prev) =>
      prev.includes(guiaId) ? prev.filter((item) => item !== guiaId) : [...prev, guiaId],
    );
  };

  const handleStartReception = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const data = await startReception(id);
      setDetail(data);
      setSuccessMessage('Recepción iniciada. Ya podés controlar físicamente la foja.');
      requestAnimationFrame(() => scanInputRef.current?.focus());
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'No se pudo iniciar la recepción.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScan = async () => {
    const raw = scanValue.trim();
    if (!raw) return;
    setIsSubmitting(true);
    setErrorMessage('');
    setScanMessage('');
    try {
      const guide = guias.find((item) => String(item.numero_guia) === raw || String(item.guia_id) === raw);
      const data = await registerFojaScan({
        foja_id: id,
        guia_id: guide?.guia_id || null,
        guia_qr: guide ? null : raw,
        qr_raw: raw,
        tipo_scan: 'descarga_destino',
      });
      setDetail(data.foja);
      const updatedSelected = (data.foja?.guias || [])
        .filter((item) => Number(item.recibida || 0) === 1)
        .map((item) => item.guia_id);
      setSelectedIds(updatedSelected);
      setScanValue('');
      setScanMessage(data.duplicated ? 'La guía ya estaba escaneada.' : 'Scan registrado correctamente.');
      requestAnimationFrame(() => scanInputRef.current?.focus());
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'No se pudo registrar el scan.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseReception = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const data = await closeReception(id, {
        modo_recepcion: modoRecepcion,
        marcar_todo: canMarkAll && selectedIds.length === guias.length,
        guia_ids_recibidas: selectedIds,
        observaciones,
        diferencias: Object.entries(differences)
          .filter(([, value]) => value)
          .map(([guiaId, value]) => ({ guia_id: Number(guiaId), detalle: value })),
        items: guias.map((item) => ({
          guia_id: item.guia_id,
          recibida: selectedIds.includes(item.guia_id),
          observaciones_recepcion: differences[item.guia_id] || null,
        })),
      });
      setDetail(data);
      setSelectedIds((data.guias || []).filter((item) => Number(item.recibida || 0) === 1).map((item) => item.guia_id));
      setSuccessMessage(
        data.foja?.estado === 'recibida'
          ? 'Foja recibida correctamente.'
          : 'Recepción cerrada con diferencias. Las guías faltantes quedaron trazadas.',
      );
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'No se pudo cerrar la recepción.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1366px] px-4 py-6 text-sm text-[rgba(var(--fg),0.72)] md:px-5">
        Cargando recepción de foja...
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1366px] flex-col gap-4 px-4 py-4 text-[rgb(var(--fg))] md:px-5">
      <section className="rounded-[24px] border border-[rgba(var(--border),0.45)] bg-[linear-gradient(135deg,rgba(var(--panel),0.98),rgba(var(--subpanel),0.92))] p-4 shadow-[0_20px_50px_rgba(var(--shadow),0.16)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.7)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.72)]">
              <QrCode size={14} />
              Recepción de foja
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Foja #{foja?.numero || foja?.id || id}</h1>
              <p className="mt-1 max-w-3xl text-sm text-[rgba(var(--fg),0.72)]">
                La guía no queda elegible para reparto hasta que esta recepción termine formalmente.
              </p>
            </div>
          </div>
          <div className="grid gap-3 md:min-w-[380px] md:grid-cols-2">
            <div className="rounded-2xl border border-[rgba(var(--border),0.36)] bg-[rgba(var(--panel),0.76)] p-3.5">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--fg),0.5)]">Estado</p>
              <div className="mt-2">
                <Badge variant={getFojaStatusPresentation(foja?.estado).variant}>
                  {getFojaStatusPresentation(foja?.estado).label}
                </Badge>
              </div>
            </div>
            <div className="rounded-2xl border border-[rgba(var(--border),0.36)] bg-[rgba(var(--panel),0.76)] p-3.5">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--fg),0.5)]">Modo</p>
              <p className="mt-2 text-base font-semibold">{MODE_LABELS[modoRecepcion] || modoRecepcion}</p>
            </div>
          </div>
        </div>
      </section>

      {errorMessage ? <div className={STATUS_MESSAGE_CLASSNAMES.error}>{errorMessage}</div> : null}
      {successMessage ? <div className={STATUS_MESSAGE_CLASSNAMES.success}>{successMessage}</div> : null}
      {scanMessage ? <div className={STATUS_MESSAGE_CLASSNAMES.info}>{scanMessage}</div> : null}

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <section className="rounded-[28px] border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.94)] p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.8)] p-4">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--fg),0.5)]">Origen → destino operativo</p>
              <p className="mt-2 text-sm font-semibold">
                {foja?.sucursal_origen_nombre || foja?.sucursal_origen_id}
                {' → '}
                {foja?.sucursal_destino_nombre || foja?.sucursal_destino_id}
              </p>
            </div>
            <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.8)] p-4">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--fg),0.5)]">Sucursal activa</p>
              <p className="mt-2 text-sm font-semibold">{sucursal.nombre}</p>
            </div>
            <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.8)] p-4">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--fg),0.5)]">Totales</p>
              <p className="mt-2 text-sm font-semibold">
                {formatNumber(totalGuias, 0)} guías · {formatNumber(totals.bultos, 0)} bultos · {formatNumber(totals.kilos, 2)} kg
              </p>
            </div>
            <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.8)] p-4">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--fg),0.5)]">Progreso QR</p>
              <p className="mt-2 text-sm font-semibold">
                {formatNumber(scansDone, 0)} / {formatNumber(totalGuias, 0)} escaneadas
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            {modoRecepcion === 'qr_obligatorio' ? (
              <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 px-4 py-4 text-sm text-amber-50">
                QR obligatorio activo: no se puede cerrar la recepción si falta una guía sin scan.
              </div>
            ) : null}
            {notice ? (
              <div
                className={notice.tone === 'info'
                  ? 'rounded-2xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-50'
                  : 'rounded-2xl border border-red-400/40 bg-red-500/10 px-4 py-4 text-sm text-red-100'}
              >
                {notice.text}
              </div>
            ) : null}

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">
                Scan rápido
              </label>
              <div className="flex gap-3">
                <Input
                  ref={scanInputRef}
                  value={scanValue}
                  onChange={(event) => setScanValue(event.target.value)}
                  placeholder="QR de guía o número"
                  disabled={!canOperateReception}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void handleScan();
                    }
                  }}
                />
                <Button onClick={handleScan} disabled={isSubmitting || !canOperateReception}>
                  <ScanLine size={16} />
                  Scan
                </Button>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">
                Observaciones de recepción
              </label>
              <Textarea
                value={observaciones}
                onChange={(event) => setObservaciones(event.target.value)}
                placeholder="Sellos, faltantes, estado físico, observaciones documentales."
                className="min-h-[100px]"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={handleStartReception} disabled={isSubmitting || receptionStarted || !canOperateReception}>
                {receptionStarted ? 'Recepción iniciada' : 'Iniciar recepción'}
              </Button>
              {canMarkAll ? (
                <Button
                  variant="outline"
                  onClick={() => setSelectedIds(guias.map((item) => item.guia_id))}
                  disabled={isSubmitting}
                >
                  Marcar todo
                </Button>
              ) : null}
              <Button variant="strong" onClick={handleCloseReception} disabled={isSubmitting || !receptionStarted || !canOperateReception}>
                {isSubmitting ? 'Cerrando...' : 'Cerrar recepción'}
              </Button>
              <Button variant="outline" onClick={loadDetail}>
                <RefreshCw size={16} />
                Actualizar
              </Button>
              <Link to="/cargas/fojas-de-ruta" className="inline-flex items-center rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-2 text-sm font-semibold hover:bg-[rgba(var(--subpanel),0.82)]">
                Volver a consulta
              </Link>
            </div>
          </div>
        </section>

        <section className="rounded-[28px] border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.94)] p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Guías dentro de la foja</h2>
              <p className="mt-1 text-sm text-[rgba(var(--fg),0.66)]">
                Control físico y documental de cada guía.
              </p>
            </div>
            <div className="text-sm text-[rgba(var(--fg),0.66)]">
              {selectedIds.length}/{totalGuias} recibidas
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-[rgba(var(--subpanel),0.86)] text-left text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">
                <tr>
                  <th className="px-3 py-3">Recibida</th>
                  <th className="px-3 py-3">Guía</th>
                  <th className="px-3 py-3">Cliente</th>
                  <th className="px-3 py-3">Estado</th>
                  <th className="px-3 py-3">Scan</th>
                  <th className="px-3 py-3">Obs. diferencia</th>
                </tr>
              </thead>
              <tbody>
                {!guias.length ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-[rgba(var(--fg),0.6)]">
                      No hay guías asociadas a la foja.
                    </td>
                  </tr>
                ) : (
                  guias.map((guia) => (
                    <tr key={guia.guia_id} className="border-t border-[rgba(var(--border),0.3)]">
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(guia.guia_id)}
                          onChange={() => toggleGuide(guia.guia_id)}
                          disabled={!canToggle}
                        />
                      </td>
                      <td className="px-3 py-3 font-semibold">{guia.numero_guia || guia.guia_id}</td>
                      <td className="px-3 py-3">{guia.destinatario_nombre || guia.remitente_nombre || '—'}</td>
                      <td className="px-3 py-3">{guia.estado}</td>
                      <td className="px-3 py-3">
                        {Number(guia.scan_obligatorio_cumplido || 0) === 1 ? (
                          <span className="inline-flex items-center gap-1 text-emerald-300">
                            <CheckCircle2 size={14} />
                            OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-200">
                            <AlertTriangle size={14} />
                            Pendiente
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <Input
                          value={differences[guia.guia_id] || ''}
                          onChange={(event) =>
                            setDifferences((prev) => ({ ...prev, [guia.guia_id]: event.target.value }))
                          }
                          placeholder="Faltante, daño, sin remito..."
                          disabled={selectedIds.includes(guia.guia_id)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pendingGuias.length ? (
            <div className="mt-4 rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.78)] p-4 text-sm text-[rgba(var(--fg),0.72)]">
              <p className="font-semibold text-[rgb(var(--fg))]">Guías pendientes</p>
              <p className="mt-2">
                {pendingGuias.map((item) => item.numero_guia || item.guia_id).join(', ')}
              </p>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
